import React, { useState, useEffect } from 'react'
import { Link, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContextLazy'
import { Dashboard } from '../../pages/portal/Dashboard'
import { EngagementsPipeline } from '../../pages/portal/EngagementsPipeline'
import { Clients } from '../../pages/portal/Clients'
import { NewClient } from '../../pages/portal/NewClient'
import { Settings } from '../../pages/portal/Settings'
import FormsDashboard from '../../pages/portal/forms/FormsDashboard'
import AdvisorMessagesV2 from '../../pages/portal/MessagesV2'
import { MobileTest } from '../../pages/portal/MobileTest'
import { LogoutConfirmationModal } from './LogoutConfirmationModal'
import { colors } from '../../lib/colors'
import { NotificationCenter } from './NotificationCenter'
import { MobileDebug } from '../debug/MobileDebug'
import { portalFeatures, isNavigationItemVisible } from '../../config/portalFeatureFlags'
import {
  HomeIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserGroupIcon,
  ArrowLeftIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline'

const navigation = [
  { name: 'Dashboard', href: '/portal/dashboard', icon: HomeIcon, key: 'dashboard' },
  { name: 'Clients', href: '/portal/clients', icon: UserGroupIcon, roles: ['pcm_staff', 'super_admin'], key: 'clients' },
  { name: 'Forms & Docs', href: '/portal/forms', icon: ClipboardDocumentListIcon, roles: ['pcm_staff', 'super_admin'], key: 'forms' },
  { name: 'Engagements', href: '/portal/engagements', icon: BriefcaseIcon, roles: ['pcm_staff', 'super_admin'], key: 'engagements' },
  { name: 'Documents', href: '/portal/documents', icon: DocumentTextIcon, key: 'documents' },
  { name: 'Messages', href: '/portal/messages', icon: ChatBubbleLeftRightIcon, key: 'messages' },
  { name: 'Support', href: '/portal/support', icon: TicketIcon, key: 'support' },
  { name: 'Analytics', href: '/portal/analytics', icon: ChartBarIcon, roles: ['client_admin', 'pcm_staff', 'super_admin'], key: 'analytics' },
  { name: 'Settings', href: '/portal/settings', icon: Cog6ToothIcon, key: 'settings' }
]

// Page title mapping
const pageTitles: Record<string, { title: string; description?: string }> = {
  '/portal/dashboard': { title: 'Dashboard', description: 'Welcome to your portal' },
  '/portal/clients': { title: 'Clients', description: 'Manage your client relationships and information' },
  '/portal/clients/new': { title: 'Add New Client', description: 'Create a new client profile' },
  '/portal/forms': { title: 'Forms & Docs', description: 'Manage questionnaires and client documents' },
  '/portal/engagements': { title: 'Engagements', description: 'Track client engagements and pipeline' },
  '/portal/documents': { title: 'Documents', description: 'Access and manage your documents' },
  '/portal/messages': { title: 'Messages', description: 'Communicate with your team' },
  '/portal/support': { title: 'Support', description: 'Get help and support' },
  '/portal/analytics': { title: 'Analytics', description: 'View insights and reports' },
  '/portal/settings': { title: 'Settings', description: 'Manage your account settings' },
  '/portal/security': { title: 'Security Settings', description: 'Manage your account security and authentication preferences' },
  '/portal/notifications': { title: 'Notifications', description: 'Manage your notification preferences' },
  '/portal/compliance': { title: 'Compliance', description: 'Track compliance and regulatory requirements' },
};

export const PortalLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const { user, userProfile, organization, signOut, hasRole } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Fix viewport height for mobile browsers
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial height
    setViewportHeight();

    // Update on resize and orientation change
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    
    // Add portal-active class to body
    document.body.classList.add('portal-active');

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
      document.body.classList.remove('portal-active');
    };
  }, []);
  
  // Debug logging to check user profile data
  useEffect(() => {
    if (import.meta.env.DEV && userProfile) {
      console.log('🔍 User Profile in PortalLayout:', {
        id: userProfile.id,
        full_name: userProfile.full_name,
        role: userProfile.role,
        organization_id: userProfile.organization_id
      })
    }
  }, [userProfile])
  
  // Get current page title
  const getPageTitle = () => {
    // Check for exact match first
    if (pageTitles[location.pathname]) {
      return pageTitles[location.pathname];
    }
    
    // Check for client detail page
    if (location.pathname.match(/^\/portal\/clients\/[a-f0-9-]+$/)) {
      return { title: 'Client Details', description: 'View and manage client information' };
    }
    
    // Default fallback
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return { 
      title: lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, ' '), 
      description: '' 
    };
  };
  
  const currentPage = getPageTitle();

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const filteredNavigation = navigation.filter(item => {
    // First check if the feature is enabled
    if (!isNavigationItemVisible(item.key)) return false
    
    // Then check role-based access
    if (!item.roles) return true
    const hasAccess = hasRole(item.roles)
    if (import.meta.env.DEV) {
      console.log(`🔐 Navigation check for ${item.name}:`, {
        requiredRoles: item.roles,
        userRole: userProfile?.role,
        hasAccess,
        featureEnabled: isNavigationItemVisible(item.key)
      })
    }
    return hasAccess
  })

  // Close sidebar when navigating on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  return (
    <div className="portal-container flex bg-gray-100 md:h-screen md:overflow-hidden">
      {/* Mobile sidebar backdrop and container */}
      <div className={`md:hidden fixed inset-0 flex z-40 transition-opacity duration-300 ease-in-out ${
        sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
        
        {/* Sidebar panel */}
        <div className={`relative flex-1 flex flex-col max-w-xs w-full transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`} style={{ backgroundColor: colors.premium.dark }}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4 mb-6">
                <Link to="/portal/dashboard" className="flex items-center gap-3 group">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-lg transition-transform group-hover:scale-105"
                    style={{ 
                      background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                      boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                    }}
                  >
                    P
                  </div>
                  <h2 className="text-xl font-bold text-white group-hover:text-gray-200 transition-colors">PCM Portal</h2>
                </Link>
              </div>
              
              {/* Back to Website Link */}
              {portalFeatures.backToWebsite && (
                <div className="px-4 mb-6">
                  <Link
                    to="/"
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 bg-white/5 border border-white/10 transition-all hover:bg-white/10 hover:text-white hover:border-white/20"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">Back to Website</span>
                  </Link>
                </div>
              )}
              
              <nav className="px-3 space-y-1">
                <div className="mb-6">
                  <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Main</h3>
                  {filteredNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`${
                        isActive
                          ? 'text-white'
                          : 'text-gray-300 hover:text-white'
                      } group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200`}
                      style={{
                        background: isActive ? `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` : undefined,
                        boxShadow: isActive ? `0 4px 12px -2px ${colors.brand.primary}40` : undefined
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                      {item.name}
                    </Link>
                  );
                })}
                </div>
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-white/10 p-4">
              <div className="flex items-center w-full p-3 rounded-lg bg-white/5 backdrop-blur-sm">
                <div className="relative">
                  <img
                    className="inline-block h-10 w-10 rounded-full ring-2 ring-white/20"
                    src={userProfile?.avatar_url || `https://ui-avatars.com/api/?name=${userProfile?.full_name || 'User'}&background=${colors.brand.primary.slice(1)}&color=fff`}
                    alt=""
                  />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{userProfile?.full_name || user?.email || 'User'}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => setShowLogoutConfirmation(true)}
                  className="ml-3 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
      </div>

      {/* Desktop sidebar - always visible on md+ screens */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-white/10" style={{ backgroundColor: colors.premium.dark }}>
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4 mb-6">
                <Link to="/portal/dashboard" className="flex items-center gap-3 group">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-lg transition-transform group-hover:scale-105"
                    style={{ 
                      background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                      boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                    }}
                  >
                    P
                  </div>
                  <h2 className="text-xl font-bold text-white group-hover:text-gray-200 transition-colors">PCM Portal</h2>
                </Link>
              </div>
              
              {/* Back to Website Link */}
              {portalFeatures.backToWebsite && (
                <div className="px-4 mb-6">
                  <Link
                    to="/"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 bg-white/5 border border-white/10 transition-all hover:bg-white/10 hover:text-white hover:border-white/20"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">Back to Website</span>
                  </Link>
                </div>
              )}
              
              <nav className="px-3 space-y-1">
                <div className="mb-6">
                  <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Main</h3>
                  {filteredNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`${
                          isActive
                            ? 'text-white'
                            : 'text-gray-300 hover:text-white'
                        } group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200`}
                        style={{
                          background: isActive ? `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` : undefined,
                          boxShadow: isActive ? `0 4px 12px -2px ${colors.brand.primary}40` : undefined
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-white/10 p-4">
              <div className="flex items-center w-full p-3 rounded-lg bg-white/5 backdrop-blur-sm">
                <div className="relative">
                  <img
                    className="inline-block h-10 w-10 rounded-full ring-2 ring-white/20"
                    src={userProfile?.avatar_url || `https://ui-avatars.com/api/?name=${userProfile?.full_name || 'User'}&background=${colors.brand.primary.slice(1)}&color=fff`}
                    alt=""
                  />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{userProfile?.full_name || user?.email || 'User'}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => setShowLogoutConfirmation(true)}
                  className="ml-3 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="relative z-10 flex-shrink-0 flex h-20 bg-white border-b border-gray-200">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-6 flex justify-between items-center">
            <div className="flex-1 flex flex-col justify-center">
              <h1 className="text-2xl font-bold text-gray-900">{currentPage.title}</h1>
              {currentPage.description && (
                <p className="text-sm text-gray-600 mt-1">{currentPage.description}</p>
              )}
            </div>
            <div className="ml-6 flex items-center gap-4">
              <NotificationCenter />
            </div>
          </div>
        </div>
        <main className="portal-main-content flex-1 relative z-0 focus:outline-none md:overflow-y-auto">
          <Routes>
            <Route path="/" element={<Navigate to="/portal/clients" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/new" element={<NewClient />} />
            <Route path="/forms" element={<FormsDashboard />} />
            <Route path="/engagements" element={<EngagementsPipeline />} />
            <Route path="/documents" element={<div className="p-6">Documents coming soon</div>} />
            <Route path="/messages/*" element={<AdvisorMessagesV2 />} />
            <Route path="/support" element={<div className="p-6">Support coming soon</div>} />
            <Route path="/analytics" element={<div className="p-6">Analytics coming soon</div>} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/mobile-test" element={<MobileTest />} />
            <Route path="*" element={<Navigate to="/portal/clients" replace />} />
          </Routes>
        </main>
      </div>
      
      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutConfirmation}
        onConfirm={handleSignOut}
        onCancel={() => setShowLogoutConfirmation(false)}
      />
      
      {/* Mobile Debug Helper - Remove in production */}
      <MobileDebug />
    </div>
  )
}